import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, GitBranch, Hand, PenLine, Redo2, Trash2, Undo2 } from "lucide-react";
import IsoSketchCanvas, { type SketchMode } from "@/components/sketch/IsoSketchCanvas";
import PipeCutList from "@/components/sketch/PipeCutList";
import { useSketch } from "@/hooks/use-pipe-sketch";
import { MATERIALS } from "@/lib/pipe-spec";

const PipeSketch = () => {
  const s = useSketch();
  const spec = MATERIALS[s.material];
  const [mode, setMode] = useState<SketchMode>("draw");


  return (
    <div className="pb-20 md:pb-6">
      <PageHeader
        title="Pipe Sketch"
        subtitle="Single-line isometric sketch with auto weld fittings and cut list"
      />

      <div className="space-y-3 px-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={s.material} onValueChange={(v) => s.setMaterial(v as typeof s.material)}>
            <SelectTrigger className="h-9 w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(MATERIALS).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={s.size} onValueChange={s.setSize}>
            <SelectTrigger className="h-9 w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {spec.sizes.map((sz) => (
                <SelectItem key={sz.nps} value={sz.nps}>
                  {sz.nps}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
            <Button
              size="sm"
              variant={mode === "draw" ? "default" : "ghost"}
              className="h-8"
              onClick={() => setMode("draw")}
            >
              <PenLine className="mr-1 h-4 w-4" /> Draw
            </Button>
            <Button
              size="sm"
              variant={mode === "branch" ? "default" : "ghost"}
              className="h-8"
              onClick={() => setMode("branch")}
              disabled={!s.runs.length}
            >
              <GitBranch className="mr-1 h-4 w-4" /> Branch
            </Button>
            <Button
              size="sm"
              variant={mode === "pan" ? "default" : "ghost"}
              className="h-8"
              onClick={() => setMode("pan")}
            >
              <Hand className="mr-1 h-4 w-4" /> Pan
            </Button>
          </div>


          <div className="ml-auto flex items-center gap-2">
            {s.activeRunId && (
              <Button size="sm" onClick={s.finishRun}>
                <Check className="mr-1 h-4 w-4" /> Finish run
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={s.undo} disabled={!s.canUndo}>
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={s.redo} disabled={!s.canRedo}>
              <Redo2 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={s.clearAll}
              disabled={!s.runs.length}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px]">
          <div className="trade-card overflow-hidden" style={{ height: "min(70vh, 560px)" }}>
            <IsoSketchCanvas
              runs={s.runs}
              fittings={s.fittings}
              cutList={s.cutList}
              activeRunId={s.activeRunId}
              material={s.material}
              size={s.size}
              mode={mode}
              onStart={s.startRun}
              onBranch={(hostRunId, p) => {
                s.branchFrom(hostRunId, p);
                setMode("draw");
              }}
              onExtend={s.extendRun}
              onFinish={s.finishRun}
              onUndoVertex={s.removeLastVertex}
            />
          </div>
          <div className="trade-card overflow-auto" style={{ maxHeight: "min(70vh, 560px)" }}>
            <PipeCutList cutList={s.cutList} totals={s.totals} />
          </div>
        </div>

        <div className="space-x-3 text-center text-xs text-muted-foreground">
          <span>Click to set the start point, then click along an axis to run pipe</span>
          <span>Turns auto-insert a BW LR elbow</span>
          <span>Change the size mid-run to drop in a concentric reducer</span>
          <span>Branch mode tees off an existing run</span>
          <span>Tap to place points · drag one finger to pan · pinch to zoom</span>
          <span>Pan mode (or shift-drag / middle-drag) moves the view without drawing</span>
          <span>Enter/Esc finish · Backspace undo · Scroll zoom · +/−/FIT buttons on the sheet</span>

        </div>

      </div>
    </div>
  );
};

export default PipeSketch;
